import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import AuditTable from './components/AuditTable'
import AuditCategory, {
  auditCategoriesByGameVersion,
} from 'app/frontend/types/custom/AuditCategory'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useTranslation } from 'react-i18next'
import { useMemo, useState } from 'react'
import { usePageInfo, useTeamAuditData } from 'app/frontend/base/config/queries'
import { AuditContext } from './context'
import { useSearchParams } from 'react-router-dom'
import { useAuditFilterState } from './components/AuditFilter'
import { Button } from '@/components/ui/button'
import { RefreshCw, Table } from 'lucide-react'
import SpreadsheetDialog from './components/SpreadsheetDialog'
import RefreshStatusDialog from './components/RefreshStatusDialog'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import Timestamp from 'app/frontend/base/components/Timestamp'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import InactiveGuildAlert from '../../components/InactiveGuildAlert'
import { toAuditRowData } from './types'
import { auditTabClassName } from './utils'
import { cn } from '@/lib/utils'
import {
  gameVersionConfig,
  gameVersionHas,
} from 'app/frontend/types/custom/GameVersion'
dayjs.extend(RelativeTime)

const TeamAudit = () => {
  const {
    data: { currentSeason, gameVersion },
  } = usePageInfo()

  const activeCategories = auditCategoriesByGameVersion[gameVersion]
  const isAuditCategory = (value: string): value is AuditCategory =>
    activeCategories.includes(value as AuditCategory)

  const { team, guild } = useTeam()
  const [, setSearchParams] = useSearchParams()
  const [selectedSeason, setSelectedSeason] = useState(currentSeason)
  const { value: weeksAgoParam, setValue: setWeeksAgoParam } =
    useAuditFilterState<string>('weeksAgo', '0')
  const weeksAgo = Number(weeksAgoParam) || 0
  const setWeeksAgo = (value: number) => setWeeksAgoParam(String(value))
  const [activeView, setActiveView] = useState<'graph' | 'list'>('list')
  const [spreadsheetOpen, setSpreadsheetOpen] = useState(false)
  const [refreshStatusOpen, setRefreshStatusOpen] = useState(false)
  const { value: visibleTeamRanks, setValue: setVisibleTeamRanks } =
    useAuditFilterState<string[]>(
      'visibleTeamRanks',
      [],
      team.teamRanks?.map((rank) => rank.id.toString()) ?? [],
    )
  const [showingRanks, setShowingRanksState] = useState(() => {
    const stored = localStorage.getItem('audit-showing-ranks')
    return stored ? JSON.parse(stored) : true
  })

  const setShowingRanks = (value: boolean) => {
    setShowingRanksState(value)
    localStorage.setItem('audit-showing-ranks', JSON.stringify(value))
  }

  const { value: activeCategory, setValue: setActiveCategory } =
    usePersistedHashValue<AuditCategory>({
      defaultValue: gameVersionConfig(gameVersion).default_audit_category,
      storageKey: 'audit-filter-activeCategory',
      isValid: isAuditCategory,
    })
  const { t } = useTranslation()

  const { data, isPending: isAuditDataLoading } = useTeamAuditData(team.id)

  const members = useMemo(() => {
    return (team.members ?? []).map((member) => ({
      ...member,
      data: toAuditRowData(data?.[member.characterReference.id]?.last_refresh),
      gear: data?.[member.characterReference.id]?.current_gear,
      keystones: data?.[member.characterReference.id]?.keystones,
      mythicRatingColor:
        data?.[member.characterReference.id]?.mythic_rating_color,
      sparkGear: data?.[member.characterReference.id]?.spark_gear,
      timeline: data?.[member.characterReference.id]?.timeline,
      uncappedCrests: data?.[member.characterReference.id]?.uncapped_crests,
    }))
  }, [data, team.members])

  const visibleMembers = useMemo(() => {
    return members.filter(
      (member) =>
        !visibleTeamRanks.length ||
        visibleTeamRanks.includes(member.teamRank?.id.toString() ?? ''),
    )
  }, [members, visibleTeamRanks])

  return (
    <Section title={`Audit | ${team.name} | ${guild.name}`}>
      <h2 className='flex items-center justify-between'>
        <span>Audit</span>
        {gameVersionHas('blizzard_api', gameVersion) ? (
          <div className='flex items-center gap-2 flex-wrap justify-end'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setSpreadsheetOpen(true)}
            >
              <Table className='h-4 w-4 mr-2' />
              View in spreadsheet
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setRefreshStatusOpen(true)}
            >
              <RefreshCw className='h-4 w-4 mr-2' />
              {team.lastRefreshedCollections ? (
                <span>
                  Refreshed{' '}
                  <Timestamp time={team.lastRefreshedCollections * 1000} />
                </span>
              ) : (
                'Refresh status'
              )}
            </Button>
          </div>
        ) : null}
      </h2>

      <SpreadsheetDialog open={spreadsheetOpen} setOpen={setSpreadsheetOpen} />
      <RefreshStatusDialog
        open={refreshStatusOpen}
        setOpen={setRefreshStatusOpen}
      />

      {activeCategories.length > 1 ? (
        <Tabs
          value={activeCategory}
          onValueChange={(value) => {
            const newCategory = value as AuditCategory
            setWeeksAgo(0)
            setSearchParams(
              (prev) => {
                prev.delete('sorting')
                return prev
              },
              { replace: true },
            )
            setActiveCategory(newCategory)
          }}
        >
          <TabsList className='w-full bg-transparent hover:bg-transparent flex justify-start gap-1.5 flex-wrap border-0'>
            {activeCategories.map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                className={cn(
                  auditTabClassName,
                  // Merges the tab into the profession tray rendered directly below the tab row, like a folder tab
                  category === 'professions' &&
                    'relative data-[state=active]:rounded-b-none data-[state=active]:border-transparent data-[state=active]:bg-muted data-[state=active]:hover:bg-muted data-[state=active]:after:absolute data-[state=active]:after:-inset-x-px data-[state=active]:after:top-full data-[state=active]:after:h-3 data-[state=active]:after:bg-muted',
                )}
              >
                <div>{t(`audit.categories.${category}.label`)}</div>
                <div className='text-xs text-muted-foreground font-normal'>
                  {t(`audit.categories.${category}.caption`)}
                </div>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      ) : null}

      <InactiveGuildAlert />

      <AuditContext.Provider
        value={{
          loadingAuditData: isAuditDataLoading,
          selectedSeason,
          setSelectedSeason,
          visibleMembers,
          members,
          weeksAgo,
          setWeeksAgo,
          showingRanks,
          setShowingRanks,
          setRefreshStatusOpen,
          activeView,
          setActiveView,
          visibleTeamRanks,
          setVisibleTeamRanks,
        }}
      >
        <AuditTable key={activeCategory} category={activeCategory} />
      </AuditContext.Provider>
    </Section>
  )
}

export default TeamAudit
